// SPDX-FileCopyrightText: 2026 mmayhew
// SPDX-License-Identifier: AGPL-3.0-only

// ── Mobile chrome controller ──
// Owns the mobile-only UI: progress bar, recent peek row,
// bottom-sheet menu from the hamburger, and the keyboard-aware edit helper
// row. Loaded after dom.js, state.js, ui_helpers.js, history.js, tabs.js,
// app.js, controller.js, shell_chrome.js so every helper it delegates to is
// already defined.

import {
  getActiveTab as importedGetActiveTab,
  getActiveTabId as importedGetActiveTabId,
  getAppState as importedGetAppState,
  onUiEvent as importedOnUiEvent,
} from './core/state.js';
import {
  copyTextToClipboard as importedCopyTextToClipboard,
  shareUrl as importedShareUrl,
  showToast as importedShowToast,
} from './core/utils.js';
import { dispatchMobileMenuAction as importedDispatchMobileMenuAction } from './features/mobile/mobile_menu_actions.js';
import { activeTeamScopeCan as importedActiveTeamScopeCan } from './features/team_scope.js';
import {
  _getStarred as importedGetStarred,
  _toggleStar as importedToggleStar,
} from './features/history/history_actions.js';
import {
  historyClearContext as importedHistoryClearContext,
  openHistoryWithFilters as importedOpenHistoryWithFilters,
  resetHistoryMobileFilters as importedResetHistoryMobileFilters,
} from './history.js';
import { confirmHistAction as importedConfirmHistAction } from './features/history/history_mutations.js';
import {
  _historyAddRunToActiveProject as importedHistoryAddRunToActiveProject,
  _historyAddRunToProject as importedHistoryAddRunToProject,
} from './features/history/history_project_actions.js';
import { restoreHistoryRunIntoTab as importedRestoreHistoryRunIntoTab } from './features/history/history_restore.js';
import {
  _historyEditEntityMetadata as importedHistoryEditEntityMetadata,
  _historyRelativeTime as importedHistoryRelativeTime,
} from './features/history/history_rows.js';
import { apiFetch as importedApiFetch, logClientError as importedLogClientError } from './session.js';
import { openStatusMonitor as importedOpenStatusMonitor } from './runtime_bridge.js';
import { openHistoryRunDetails as importedOpenHistoryRunDetails } from './features/history/history_run_modal_state_bridge.js';
import { openHistoryCompareLauncher as importedOpenHistoryCompareLauncher } from './features/run-comparison/history_compare_bridge.js';
import { performMobileEditAction as importedPerformMobileEditAction } from './app.js';
import { confirmKill as importedConfirmKill } from './runner.js';
import {
  getActiveProjectContext as importedGetActiveProjectContext,
  refreshActiveProjectContext as importedRefreshActiveProjectContext,
} from './features/projects/project_context_bridge.js';
import { getAppConfig as importedGetAppConfig } from './core/config.js';
import { loadMobileRunningIndicator as importedLoadMobileRunningIndicator } from './core/lazy_assets.js';
import { bindMobileSheet as importedBindMobileSheet } from './ui/mobile_sheet.js';
import { bindDisclosure as importedBindDisclosure } from './ui/ui_disclosure.js';
import { bindDismissible as importedBindDismissible } from './ui/ui_dismissible.js';
import { bindOutsideClickClose as importedBindOutsideClickClose } from './ui/ui_outside_click.js';
import { bindPressable as importedBindPressable } from './ui/ui_pressable.js';
import {
  blurVisibleComposerInputIfMobile as importedBlurVisibleComposerInputIfMobile,
  setComposerValue as importedSetComposerValue,
} from './ui/ui_helpers.js';

(function initMobileChrome(global) {
  if (typeof document === 'undefined') return;

  // Bail on desktop-only builds: every mobile-specific node below is absent
  // when the page is rendered without the mobile shell.
  const mobileShell = document.getElementById('mobile-shell');
  if (!mobileShell) return;

  const activeTeamScopeCan = (typeof importedActiveTeamScopeCan !== 'undefined' && importedActiveTeamScopeCan) || null;
  const apiFetch = (typeof importedApiFetch !== 'undefined' && importedApiFetch) || null;
  const bindDisclosure = (typeof importedBindDisclosure !== 'undefined' && importedBindDisclosure) || null;
  const bindDismissible = (typeof importedBindDismissible !== 'undefined' && importedBindDismissible) || null;
  const bindMobileSheet = (typeof importedBindMobileSheet !== 'undefined' && importedBindMobileSheet) || null;
  const bindOutsideClickClose = (typeof importedBindOutsideClickClose !== 'undefined' && importedBindOutsideClickClose) || null;
  const bindPressable = (typeof importedBindPressable !== 'undefined' && importedBindPressable) || null;
  const blurVisibleComposerInputIfMobile = (
    typeof importedBlurVisibleComposerInputIfMobile !== 'undefined'
    && importedBlurVisibleComposerInputIfMobile
  ) || null;
  const confirmHistAction = (typeof importedConfirmHistAction !== 'undefined' && importedConfirmHistAction) || null;
  const confirmKill = (typeof importedConfirmKill !== 'undefined' && importedConfirmKill) || null;
  const copyTextToClipboard = (typeof importedCopyTextToClipboard !== 'undefined' && importedCopyTextToClipboard) || null;
  const dispatchMobileMenuAction = (
    typeof importedDispatchMobileMenuAction !== 'undefined'
    && importedDispatchMobileMenuAction
  ) || null;
  const getActiveTab = (typeof importedGetActiveTab !== 'undefined' && importedGetActiveTab) || null;
  const getActiveTabId = (typeof importedGetActiveTabId !== 'undefined' && importedGetActiveTabId) || null;
  const getAppState = (typeof importedGetAppState !== 'undefined' && importedGetAppState) || null;
  const getStarred = (typeof importedGetStarred !== 'undefined' && importedGetStarred) || null;
  const historyAddRunToActiveProject = (
    typeof importedHistoryAddRunToActiveProject !== 'undefined'
    && importedHistoryAddRunToActiveProject
  ) || null;
  const historyAddRunToProject = (
    typeof importedHistoryAddRunToProject !== 'undefined'
    && importedHistoryAddRunToProject
  ) || null;
  const historyEditEntityMetadata = (
    typeof importedHistoryEditEntityMetadata !== 'undefined'
    && importedHistoryEditEntityMetadata
  ) || null;
  const historyClearContext = (
    typeof importedHistoryClearContext !== 'undefined'
    && importedHistoryClearContext
  ) || null;
  const historyRelativeTime = (typeof importedHistoryRelativeTime !== 'undefined' && importedHistoryRelativeTime) || null;
  const onUiEvent = (typeof importedOnUiEvent !== 'undefined' && importedOnUiEvent) || null;
  const openHistoryCompareLauncher = (typeof importedOpenHistoryCompareLauncher !== 'undefined' && importedOpenHistoryCompareLauncher) || null;
  const openHistoryRunDetails = (typeof importedOpenHistoryRunDetails !== 'undefined' && importedOpenHistoryRunDetails) || null;
  const openHistoryWithFilters = (typeof importedOpenHistoryWithFilters !== 'undefined' && importedOpenHistoryWithFilters) || null;
  const performMobileEditAction = (typeof importedPerformMobileEditAction !== 'undefined' && importedPerformMobileEditAction) || null;
  const resetHistoryMobileFilters = (typeof importedResetHistoryMobileFilters !== 'undefined' && importedResetHistoryMobileFilters) || null;
  const restoreHistoryRunIntoTab = (
    typeof importedRestoreHistoryRunIntoTab !== 'undefined'
    && importedRestoreHistoryRunIntoTab
  ) || null;
  const setComposerValue = (typeof importedSetComposerValue !== 'undefined' && importedSetComposerValue) || null;
  const shareUrl = (typeof importedShareUrl !== 'undefined' && importedShareUrl) || null;
  const showToast = (typeof importedShowToast !== 'undefined' && importedShowToast) || null;
  const toggleStar = (typeof importedToggleStar !== 'undefined' && importedToggleStar) || null;

  function logMobileChromeError(context, err) {
    if (typeof importedLogClientError === 'function') importedLogClientError(context, err);
  }

  function _recentsOpenHistoryCompare(run, options = {}) {
    const hasImportedHandler = typeof openHistoryCompareLauncher?.hasHandler === 'function'
      && openHistoryCompareLauncher.hasHandler();
    const openCompare = hasImportedHandler || typeof openHistoryCompareLauncher?.hasHandler !== 'function'
      ? openHistoryCompareLauncher
      : null;
    if (typeof openCompare !== 'function') return false;
    try {
      const result = openCompare(run, options);
      return result !== false;
    } catch (err) {
      logMobileChromeError('mobile recents compare unavailable', err);
      return false;
    }
  }

  function _recentsOpenRunDetails(run) {
    const hasImportedHandler = typeof openHistoryRunDetails?.hasHandler === 'function'
      && openHistoryRunDetails.hasHandler();
    const openDetails = hasImportedHandler || typeof openHistoryRunDetails?.hasHandler !== 'function'
      ? openHistoryRunDetails
      : null;
    if (typeof openDetails !== 'function') return false;
    try {
      const result = openDetails(run);
      return result !== false;
    } catch (err) {
      logMobileChromeError('mobile recents run details unavailable', err);
      return false;
    }
  }

  // ── Elements ────────────────────────────────────────────────────
  const mobileShellChrome     = document.getElementById('mobile-shell-chrome');
  const mobileComposer        = document.getElementById('mobile-composer');
  const mobileKillBtn         = document.getElementById('mobile-kill-btn');
  const statusPillEl          = document.getElementById('status');
  const recentPeek            = document.getElementById('mobile-recent-peek');
  const recentPeekCount       = document.getElementById('mobile-recent-peek-count');
  const recentPeekPreview     = document.getElementById('mobile-recent-peek-preview');
  const recentsSheet          = document.getElementById('mobile-recents-sheet');
  const recentsSheetScrim     = document.getElementById('mobile-recents-sheet-scrim');
  const recentsSheetClearBtn  = document.getElementById('mobile-recents-clear');
  const recentsSheetSearch    = document.getElementById('mobile-recents-search');
  const recentsPagination     = document.getElementById('mobile-recents-pagination');
  const recentsPaginationSummary = document.getElementById('mobile-recents-pagination-summary');
  const recentsPaginationControls = document.getElementById('mobile-recents-pagination-controls');
  const recentsSheetList      = document.getElementById('mobile-recents-list');
  const menuSheet             = document.getElementById('mobile-menu-sheet');
  const menuSheetScrim        = document.getElementById('mobile-menu-sheet-scrim');
  const menuLnState           = document.getElementById('mobile-menu-ln-state');
  const menuTsState           = document.getElementById('mobile-menu-ts-state');
  const menuWorkflowsCount    = document.getElementById('mobile-menu-workflows-count');
  const menuSchedulesCount    = document.getElementById('mobile-menu-schedules-count');
  const menuWatchersCount     = document.getElementById('mobile-menu-watchers-count');
  const menuHistoryCount      = document.getElementById('mobile-menu-history-count');
  const menuAtlasHint         = document.getElementById('mobile-menu-atlas-hint');
  const menuFilesHint         = document.getElementById('mobile-menu-files-hint');
  const menuProjectHint       = document.getElementById('mobile-menu-project-hint');
  const menuThemeHint         = document.getElementById('mobile-menu-theme-hint');
  const kbHelper              = document.getElementById('mobile-kb-helper');

  // ── Progress bar mounted programmatically ──────────────────────
  // Placed inside #mobile-shell-chrome so the teleport logic in app.js that
  // moves the tab bar in and out on viewport changes does not clobber them.
  let progressBar  = null;

  function ensureChromeMounts() {
    if (!mobileShellChrome) return;
    if (!progressBar) {
      progressBar = document.createElement('div');
      progressBar.id = 'mobile-progress-bar';
      progressBar.className = 'shell-progress-bar u-hidden';
      mobileShellChrome.appendChild(progressBar);
    }
  }
  ensureChromeMounts();

  // ── Helpers ─────────────────────────────────────────────────────
  const show = (el) => el && el.classList && el.classList.remove('u-hidden');
  const hide = (el) => el && el.classList && el.classList.add('u-hidden');
  const isRunning = () => !!(statusPillEl && statusPillEl.classList && statusPillEl.classList.contains('running'));
  const STATUS_MONITOR_PEEK_PULSE_KEY = 'status_monitor_mobile_peek_seen';
  let _statusMonitorPeekHoldUntil = 0;
  let _statusMonitorPeekTimer = 0;

  // ── 2A+2B: Status-driven progress bar and composer ring ─────────
  function syncRunState() {
    const running = isRunning();
    if (running) {
      show(progressBar);
      if (mobileComposer && mobileComposer.classList) mobileComposer.classList.add('is-running');
    } else {
      hide(progressBar);
      if (mobileComposer && mobileComposer.classList) mobileComposer.classList.remove('is-running');
    }
    // Mobile only: the desktop pill intentionally shows only IDLE/RUNNING
    // (HUD LAST EXIT carries the rest), but with no HUD on mobile, an IDLE
    // label sitting inside a red killed/fail pill reads as a bug. Reflect the
    // terminal state in the pill text so the color and label agree. Gated on
    // body.mobile-terminal-mode so the desktop pill stays binary when the
    // mobile DOM is present but the shell is rendering desktop chrome.
    if (statusPillEl && statusPillEl.classList && document.body.classList.contains('mobile-terminal-mode')) {
      if (statusPillEl.classList.contains('killed')) statusPillEl.textContent = 'KILLED';
      else if (statusPillEl.classList.contains('fail')) statusPillEl.textContent = 'FAILED';
    }
  }

  if (mobileKillBtn) {
    mobileKillBtn.addEventListener('click', () => {
      const tabId = typeof getActiveTabId === 'function' ? getActiveTabId() : null;
      if (tabId && typeof confirmKill === 'function') confirmKill(tabId);
    });
  }
  if (typeof onUiEvent === 'function') {
    onUiEvent('app:status-changed', () => syncRunState());
  }
  syncRunState();

  // ── Mobile non-active running-state indicator ──
  const runningIndicatorDisabled = (() => {
    try {
      const q = (typeof location !== 'undefined' && location.search) ? location.search : '';
      return /[?&]ri=(?:off|0)\b/.test(q);
    } catch (_) { return false; }
  })();
  const tabsBarEl = runningIndicatorDisabled ? null : document.getElementById('tabs-bar');
  let mobileRunningIndicatorPromise = null;
  function mobileRunningIndicatorActive() {
    return !!(document.body && document.body.classList.contains('mobile-terminal-mode'));
  }
  function ensureMobileRunningIndicator() {
    if (
      !tabsBarEl
      || (typeof tabsBarEl.isConnected === 'boolean' && !tabsBarEl.isConnected)
      || !mobileRunningIndicatorActive()
      || typeof importedLoadMobileRunningIndicator !== 'function'
    ) return null;
    if (mobileRunningIndicatorPromise) return mobileRunningIndicatorPromise;
    mobileRunningIndicatorPromise = importedLoadMobileRunningIndicator()
      .then((indicator) => {
        indicator?.create?.({
          tabsBarEl,
          terminalBarEl: tabsBarEl.closest('.terminal-bar'),
        });
      })
      .catch((err) => {
        mobileRunningIndicatorPromise = null;
        logMobileChromeError('failed to load mobile running indicator', err);
      });
    return mobileRunningIndicatorPromise;
  }
  ensureMobileRunningIndicator();
  if (tabsBarEl && typeof document.addEventListener === 'function') {
    document.addEventListener('app:mobile-terminal-mode-changed', (event) => {
      if (event?.detail?.active) ensureMobileRunningIndicator();
    });
  }

  // ── 2C: Menu sheet ───────────────────────────────────────────────
  function setActionHint(el, text) {
    if (el) el.textContent = text || '';
  }
  function setTogglePressed(labelEl, value) {
    if (!labelEl) return;
    const btn = labelEl.closest('button[data-menu-action]');
    if (!btn) return;
    // Any value other than empty/off counts as "on" — line numbers toggles on/off,
    // but timestamps cycles through off / elapsed / clock so we can't match /^on$/.
    const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
    const on = normalized && normalized !== 'off';
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
  }
  function refreshMenuStateHints() {
    // Read current modes from body classes — the canonical source of truth set
    // by output.js / app.js when the user toggles either preference.
    const cls = (document.body && document.body.classList) || null;
    const lnValue = cls && cls.contains('ln-on') ? 'on' : 'off';
    let tsValue = 'off';
    if (cls && cls.contains('ts-elapsed')) tsValue = 'elapsed';
    else if (cls && cls.contains('ts-clock')) tsValue = 'clock';
    setActionHint(menuLnState, lnValue);
    setTogglePressed(menuLnState, lnValue);
    setActionHint(menuTsState, tsValue);
    setTogglePressed(menuTsState, tsValue);
    // Sync the timestamps sub-menu: pressed state on the currently-selected
    // mode, unpressed on the other two. Aria-pressed drives the radio styling.
    menuSheet?.querySelectorAll('[data-menu-action="ts-set"]').forEach((btn) => {
      const isActive = btn.dataset.tsMode === tsValue;
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }
  function refreshWorkflowsCount(items) {
    if (!menuWorkflowsCount) return;
    const list = Array.isArray(items) ? items : [];
    menuWorkflowsCount.textContent = list.length ? `${list.length} saved` : '';
  }
  function setSavedCount(el, count) {
    if (!el) return;
    const total = Math.max(0, Number(count || 0));
    el.textContent = total > 0 ? `${total} saved` : '';
  }
  function pluralCount(count, singular, plural = `${singular}s`) {
    const total = Math.max(0, Number(count || 0));
    if (!total) return '';
    return `${total} ${total === 1 ? singular : plural}`;
  }
  let schedulesCountRequestSeq = 0;
  function refreshSchedulesCount(items = null) {
    if (!menuSchedulesCount) return;
    if (Array.isArray(items)) {
      setSavedCount(menuSchedulesCount, items.length);
      return;
    }
    if (typeof apiFetch !== 'function') return;
    const requestSeq = ++schedulesCountRequestSeq;
    apiFetch('/schedules', { cache: 'no-store' })
      .then(resp => resp && resp.ok === false ? Promise.reject(new Error(`HTTP ${resp.status}`)) : resp.json())
      .then(data => {
        if (requestSeq !== schedulesCountRequestSeq) return;
        setSavedCount(menuSchedulesCount, Array.isArray(data?.schedules) ? data.schedules.length : 0);
      })
      .catch((err) => {
        logMobileChromeError('failed to load schedules count for mobile menu', err);
      });
  }
  let watchersCountRequestSeq = 0;
  function refreshWatchersCount(items = null) {
    if (!menuWatchersCount) return;
    if (Array.isArray(items)) {
      setSavedCount(menuWatchersCount, items.length);
      return;
    }
    if (typeof apiFetch !== 'function') return;
    const requestSeq = ++watchersCountRequestSeq;
    apiFetch('/watchers', { cache: 'no-store' })
      .then(resp => resp && resp.ok === false ? Promise.reject(new Error(`HTTP ${resp.status}`)) : resp.json())
      .then(data => {
        if (requestSeq !== watchersCountRequestSeq) return;
        setSavedCount(menuWatchersCount, Array.isArray(data?.watchers) ? data.watchers.length : 0);
      })
      .catch((err) => {
        logMobileChromeError('failed to load watchers count for mobile menu', err);
      });
  }
  let historyCountRequestSeq = 0;
  function setMenuHistoryCount(count) {
    const total = Number(count || 0);
    menuHistoryCount.textContent = total > 0 ? `${total} saved` : '';
  }
  function _recentsTotalCountFromCache() {
    if (!_recentsLoaded) return null;
    const total = Number(_recentsPaging.totalCount || 0);
    return Number.isFinite(total) ? Math.max(0, total) : 0;
  }
  function refreshHistoryCount() {
    if (!menuHistoryCount) return;
    const runs = readCmdHistory();
    setMenuHistoryCount(runs.length);
    const requestSeq = ++historyCountRequestSeq;
    if (typeof apiFetch !== 'function') return;
    apiFetch('/history', { cache: 'no-store' })
      .then(resp => resp && resp.ok === false ? Promise.reject(new Error(`HTTP ${resp.status}`)) : resp.json())
      .then(data => {
        if (requestSeq !== historyCountRequestSeq) return;
        const total = Number(data?.total_count ?? data?.items?.length ?? data?.runs?.length ?? 0);
        setMenuHistoryCount(Number.isFinite(total) ? total : 0);
      })
      .catch((err) => {
        logMobileChromeError('failed to load history count for mobile menu', err);
      });
  }
  let atlasHintRequestSeq = 0;
  function refreshAtlasHint() {
    if (!menuAtlasHint || typeof apiFetch !== 'function') return;
    const requestSeq = ++atlasHintRequestSeq;
    apiFetch('/atlas?orphan_filter=hide&suppression_filter=hide', { cache: 'no-store' })
      .then(resp => resp && resp.ok === false ? Promise.reject(new Error(`HTTP ${resp.status}`)) : resp.json())
      .then(data => {
        if (requestSeq !== atlasHintRequestSeq) return;
        const total = Math.max(0, Number(data?.total || 0));
        const findings = Math.max(0, Number(data?.findings || 0));
        menuAtlasHint.textContent = total
          ? pluralCount(total, 'entity', 'entities')
          : pluralCount(findings, 'finding');
      })
      .catch((err) => {
        logMobileChromeError('failed to load Atlas count for mobile menu', err);
      });
  }
  let filesHintRequestSeq = 0;
  function refreshFilesHint() {
    if (!menuFilesHint || typeof apiFetch !== 'function') return;
    const requestSeq = ++filesHintRequestSeq;
    apiFetch('/workspace/files', { cache: 'no-store' })
      .then(resp => resp && resp.ok === false ? Promise.reject(new Error(`HTTP ${resp.status}`)) : resp.json())
      .then(data => {
        if (requestSeq !== filesHintRequestSeq) return;
        const usageCount = Number(data?.usage?.file_count);
        const files = Number.isFinite(usageCount) ? usageCount : (Array.isArray(data?.files) ? data.files.length : 0);
        menuFilesHint.textContent = pluralCount(files, 'file');
      })
      .catch((err) => {
        logMobileChromeError('failed to load Files count for mobile menu', err);
      });
  }
  function _projectHintName(project) {
    if (!project || typeof project !== 'object') return '';
    return String(project.name || project.slug || project.id || '').trim();
  }
  function refreshProjectHint(project) {
    if (!menuProjectHint) return;
    const current = project || (typeof importedGetActiveProjectContext === 'function'
      ? importedGetActiveProjectContext()
      : null);
    const name = _projectHintName(current);
    menuProjectHint.textContent = name;
    menuProjectHint.title = name ? `Active project: ${name}` : '';
  }
  function refreshProjectHintFromServer() {
    refreshProjectHint();
    if (typeof importedRefreshActiveProjectContext !== 'function') return;
    importedRefreshActiveProjectContext()
      .then(project => refreshProjectHint(project))
      .catch((err) => {
        logMobileChromeError('failed to load active project for mobile menu', err);
      });
  }
  function refreshThemeHint() {
    if (!menuThemeHint) return;
    const name = (document.body && document.body.dataset && document.body.dataset.theme) || '';
    menuThemeHint.textContent = name;
  }
  // Bind the timestamps sub-menu as a disclosure so aria-expanded and the
  // submenu's u-hidden class stay coordinated. The handle is also used by
  // openMenuSheet() to reset the sub-menu to collapsed each time the sheet
  // opens (so the user never returns to a previously-expanded surface).
  const tsToggleBtn = menuSheet?.querySelector('[data-menu-action="ts-toggle"]');
  const compareActiveBtn = menuSheet?.querySelector('[data-menu-action="compare-active"]');
  const tsSubmenuEl = document.getElementById('mobile-menu-ts-submenu');
  const tsDisclosure = tsToggleBtn ? bindDisclosure(tsToggleBtn, {
    panel: tsSubmenuEl,
    openClass: null,
    hiddenClass: 'u-hidden',
  }) : null;

  function activeComparisonRun() {
    const tab = typeof getActiveTab === 'function' ? getActiveTab() : null;
    const runId = String(tab?.historyRunId || '').trim();
    const runKind = String(tab?.historyRunKind || '').trim();
    if (!runId || tab?.st === 'running' || runKind !== 'external') return null;
    return { id: runId };
  }

  function refreshCompareActiveAction() {
    compareActiveBtn?.classList.toggle('u-hidden', !activeComparisonRun());
  }

  function openMenuSheet() {
    refreshMenuStateHints();
    refreshThemeHint();
    refreshHistoryCount();
    refreshAtlasHint();
    refreshFilesHint();
    refreshSchedulesCount();
    refreshWatchersCount();
    refreshProjectHintFromServer();
    refreshCompareActiveAction();
    tsDisclosure?.close();
    show(menuSheetScrim);
    show(menuSheet);
  }
  function closeMenuSheet() {
    hide(menuSheet);
    hide(menuSheetScrim);
  }
  function isMenuSheetOpen() {
    return !!(menuSheet && menuSheet.classList && !menuSheet.classList.contains('u-hidden'));
  }

  // Take over shared mobile-menu events so callers that imported the generic
  // helpers before this module loaded still open the sheet-aware menu.
  if (typeof onUiEvent === 'function') {
    onUiEvent('app:mobile-menu-show', openMenuSheet);
    onUiEvent('app:mobile-menu-hide', closeMenuSheet);
  }

  function openMobileHistorySurface() {
    if (typeof resetHistoryMobileFilters === 'function') {
      resetHistoryMobileFilters();
    }
    if (typeof openHistoryWithFilters === 'function') {
      openHistoryWithFilters();
    } else if (typeof dispatchMobileMenuAction === 'function') {
      dispatchMobileMenuAction('history', null);
    } else {
      showRecentsSheet();
    }
  }

  // Mobile routes both History entry points to the full History panel so the
  // quick path and menu path expose the same filtering and bulk controls.
  menuSheet?.querySelectorAll('button[data-menu-action]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const action = btn.dataset.menuAction;
      if (action === 'history') {
        e.stopImmediatePropagation();
        closeMenuSheet();
        openMobileHistorySurface();
      } else if (action === 'compare-active') {
        e.preventDefault();
        e.stopImmediatePropagation();
        const run = activeComparisonRun();
        closeMenuSheet();
        if (run) {
          _recentsOpenHistoryCompare(run, {
            returnFocus: document.getElementById('hamburger-btn'),
          });
        }
      }
    }, true);
  });
  // Scrim click + Escape are owned by bindDismissible
  // (ui_dismissible.js) so every sheet/panel/modal surface uses the same
  // registry-driven close cascade instead of hand-rolled wiring.
  if (typeof bindDismissible === 'function') {
    bindDismissible(menuSheet, {
      level: 'sheet',
      isOpen: isMenuSheetOpen,
      onClose: closeMenuSheet,
      backdropEl: menuSheetScrim,
    });
  }

  // ── 2D: Recent peek ─────────────────────────────────────────────
  function readCmdHistory() {
    const h = typeof getAppState === 'function' ? getAppState().recentPreviewHistory : null;
    return Array.isArray(h) ? h : [];
  }
  function _prefersReducedMotion() {
    try {
      return !!(global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches);
    } catch (_) {
      return false;
    }
  }
  function _formatPeekElapsed(runStart) {
    const start = Number(runStart);
    if (!Number.isFinite(start) || start <= 0) return '';
    const seconds = Math.max(0, Math.floor((Date.now() - start) / 1000));
    const minutes = Math.floor(seconds / 60);
    const remainder = String(seconds % 60).padStart(2, '0');
    return `${minutes}:${remainder}`;
  }
  function _syncStatusMonitorPeekTimer(activeRunning) {
    if (activeRunning) {
      if (!_statusMonitorPeekTimer) {
        _statusMonitorPeekTimer = window.setInterval(() => {
          try { renderRecentPeek(); } catch (_) { /* non-critical */ }
        }, 1000);
      }
      return;
    }
    if (_statusMonitorPeekTimer) {
      window.clearInterval(_statusMonitorPeekTimer);
      _statusMonitorPeekTimer = 0;
    }
  }
  function renderRecentPeek() {
    if (!recentPeek) return;
    const activeTab = typeof getActiveTab === 'function' ? getActiveTab() : null;
    const activeRunning = !!(activeTab && activeTab.st === 'running');
    _syncStatusMonitorPeekTimer(activeRunning);
    const holdStatusMonitor = !activeRunning && _statusMonitorPeekHoldUntil && Date.now() < _statusMonitorPeekHoldUntil;
    if (activeRunning || holdStatusMonitor) {
      recentPeek.dataset.peekMode = 'status-monitor';
      recentPeek.setAttribute('aria-label', 'Open Status Monitor');
      const elapsed = activeRunning ? _formatPeekElapsed(activeTab.runStart) : '';
      if (recentPeekCount) recentPeekCount.textContent = activeRunning ? (elapsed || 'live') : 'done';
      if (recentPeekPreview) {
        recentPeekPreview.textContent = activeRunning
          ? String(activeTab.command || 'active command')
          : 'final state available';
      }
      const label = recentPeek.querySelector('.recent-peek-label');
      if (label) label.textContent = 'Status Monitor';
      show(recentPeek);
      if (activeRunning && !_prefersReducedMotion()) {
        try {
          if (sessionStorage.getItem(STATUS_MONITOR_PEEK_PULSE_KEY) !== '1') {
            sessionStorage.setItem(STATUS_MONITOR_PEEK_PULSE_KEY, '1');
            recentPeek.classList.add('recent-peek-status-monitor-wiggle');
            window.setTimeout(() => recentPeek.classList.remove('recent-peek-status-monitor-wiggle'), 1900);
          }
        } catch (_) {
          if (recentPeek.dataset.statusMonitorWiggled !== '1') {
            recentPeek.dataset.statusMonitorWiggled = '1';
            recentPeek.classList.add('recent-peek-status-monitor-wiggle');
            window.setTimeout(() => recentPeek.classList.remove('recent-peek-status-monitor-wiggle'), 1900);
          }
        }
      }
      return;
    }
    recentPeek.dataset.peekMode = 'recents';
    recentPeek.setAttribute('aria-label', 'Show recent commands');
    const label = recentPeek.querySelector('.recent-peek-label');
    if (label) label.textContent = 'Recent';
    const items = readCmdHistory();
    if (!items.length) { hide(recentPeek); return; }
    if (recentPeekCount) recentPeekCount.textContent = String(items.length);
    if (recentPeekPreview) recentPeekPreview.textContent = items.slice(0, 3).join(' · ');
    show(recentPeek);
  }

  // ── 2D+: Pull-up recents sheet ─────────────────────────────────
  // Populated on open from the /history API so the list reflects persisted
  // runs (not just the in-memory cmdHistory chip list). Row click rehydrates
  // the composer; per-row actions reuse the existing history.js helpers.
  let _recentsItems = [];
  let _recentsSearchQuery = '';
  let _recentsLoaded = false;
  let _recentsFetchInFlight = null;
  let _recentsRequestSeq = 0;
  const _recentsFilterState = { type: 'all', root: '', exit: 'all', date: 'all', starred: false };
  const _recentsPaging = {
    page: 1,
    pageSize: (importedGetAppConfig?.() && importedGetAppConfig().history_panel_limit)
      ? Math.max(1, Number(importedGetAppConfig().history_panel_limit) || 50)
      : 50,
    totalCount: 0,
    pageCount: 0,
    hasPrev: false,
    hasNext: false,
  };

  function _recentsParseDate(iso) {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      return Number.isNaN(d.getTime()) ? null : d;
    } catch (_) { return null; }
  }
  function _recentsFiltersActiveCount() {
    let n = 0;
    if (_recentsFilterState.type !== 'all') n++;
    if (_recentsFilterState.root.trim()) n++;
    if (_recentsFilterState.exit !== 'all') n++;
    if (_recentsFilterState.date !== 'all') n++;
    if (_recentsFilterState.starred) n++;
    return n;
  }
  function _recentsStarred() {
    try {
      if (typeof getStarred === 'function') return getStarred();
    } catch (_) { /* non-critical */ }
    return new Set();
  }
  function _recentsHasActiveFilters() {
    return Boolean(
      _recentsSearchQuery.trim()
      || _recentsFilterState.type !== 'all'
      || _recentsFilterState.root.trim()
      || _recentsFilterState.exit !== 'all'
      || _recentsFilterState.date !== 'all'
      || _recentsFilterState.starred
    );
  }
  function _recentsBuildHistoryRequestUrl() {
    const params = new URLSearchParams();
    params.set('page', String(_recentsPaging.page || 1));
    params.set('page_size', String(_recentsPaging.pageSize || 1));
    params.set('include_total', '1');
    if (_recentsFilterState.type !== 'all') params.set('type', _recentsFilterState.type);
    if (_recentsSearchQuery.trim()) params.set('q', _recentsSearchQuery.trim());
    if (_recentsFilterState.root.trim()) params.set('command_root', _recentsFilterState.root.trim());
    if (_recentsFilterState.exit === 'success') params.set('exit_code', '0');
    else if (_recentsFilterState.exit === 'failed') params.set('exit_code', 'nonzero');
    if (_recentsFilterState.date === 'today') params.set('date_range', '24h');
    else if (_recentsFilterState.date === 'week') params.set('date_range', '7d');
    if (_recentsFilterState.starred) params.set('starred_only', '1');
    const query = params.toString();
    return query ? `/history?${query}` : '/history';
  }
  function _recentsDeleteContext() {
    if (typeof historyClearContext !== 'function') return null;
    return historyClearContext({
      type: _recentsFilterState.type,
      q: _recentsSearchQuery.trim(),
      commandRoot: _recentsFilterState.root.trim(),
      signal: 'all',
      kind: 'all',
      entity: '',
      entityType: 'all',
      exitCode: _recentsFilterState.exit === 'success'
        ? '0'
        : (_recentsFilterState.exit === 'failed' ? 'nonzero' : 'all'),
      dateRange: _recentsFilterState.date === 'today'
        ? '24h'
        : (_recentsFilterState.date === 'week' ? '7d' : 'all'),
      projectId: 'all',
      starredOnly: _recentsFilterState.starred,
    });
  }
  function _recentsSetPage(nextPage, { refresh = true } = {}) {
    _recentsPaging.page = Math.max(1, Number(nextPage) || 1);
    if (refresh) _recentsRefresh();
  }
  function _recentsRenderPagination(visibleCount = 0) {
    if (!recentsPagination || !recentsPaginationSummary || !recentsPaginationControls) return;
    const { page, pageSize, totalCount, pageCount } = _recentsPaging;
    const totalLabel = _recentsFilterState.type === 'runs'
      ? (totalCount === 1 ? 'stored run' : 'stored runs')
      : _recentsFilterState.type === 'snapshots'
        ? (totalCount === 1 ? 'stored snapshot' : 'stored snapshots')
        : (totalCount === 1 ? 'stored item' : 'stored items');
    if (totalCount > 0) {
      const start = ((page - 1) * pageSize) + 1;
      const count = Math.max(0, Number(visibleCount) || 0);
      const end = count > 0 ? Math.min(totalCount, start + count - 1) : start;
      recentsPaginationSummary.textContent = `Showing ${start}-${end} of ${totalCount} ${totalLabel}`;
    } else {
      recentsPaginationSummary.textContent = `Showing 0 of 0 ${totalLabel}`;
    }

    recentsPaginationControls.replaceChildren();

    const prevPage = page > 1 ? page - 1 : 1;
    const prevBtn = document.createElement('button');
    prevBtn.type = 'button';
    prevBtn.className = 'btn btn-secondary btn-compact history-pagination-chevron';
    prevBtn.textContent = '‹ Prev';
    prevBtn.disabled = page <= 1;
    prevBtn.setAttribute('aria-label', 'Previous page');
    prevBtn.addEventListener('click', () => _recentsSetPage(prevPage));
    recentsPaginationControls.appendChild(prevBtn);

    const pageLabel = document.createElement('span');
    pageLabel.className = 'history-pagination-status';
    pageLabel.textContent = `Page ${pageCount > 0 ? page : 0} of ${pageCount}`;
    pageLabel.setAttribute('aria-live', 'polite');
    recentsPaginationControls.appendChild(pageLabel);

    const nextPage = pageCount > page ? page + 1 : page;
    const nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'btn btn-secondary btn-compact history-pagination-chevron';
    nextBtn.textContent = 'Next ›';
    nextBtn.disabled = page >= pageCount;
    nextBtn.setAttribute('aria-label', 'Next page');
    nextBtn.addEventListener('click', () => _recentsSetPage(nextPage));
    recentsPaginationControls.appendChild(nextBtn);

    recentsPagination.classList.remove('u-hidden');
  }
  function _recentsMakeAction(label, handler, role = 'secondary') {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `sheet-item-action btn btn-${role} btn-compact`;
    btn.textContent = label;
    bindPressable(btn, {
      clearPressStyle: true,
      onActivate: (e) => {
        e.stopPropagation();
        try { handler(); } catch (_) { /* non-critical */ }
      },
    });
    return btn;
  }
  function _recentsCloseActionMenus(except = null) {
    recentsSheetList?.querySelectorAll('.sheet-item-action-menu-wrap.open').forEach((wrap) => {
      if (except && wrap === except) return;
      wrap.classList.remove('open');
      wrap.querySelector('.sheet-item-action-menu-trigger')?.setAttribute('aria-expanded', 'false');
      _recentsResetActionMenuPosition(wrap);
    });
  }
  function _recentsResetActionMenuPosition(wrap) {
    const menu = wrap?.querySelector?.('.sheet-item-action-menu');
    if (!menu) return;
    menu.style.position = '';
    menu.style.left = '';
    menu.style.top = '';
    menu.style.right = '';
    menu.style.bottom = '';
    menu.style.width = '';
    menu.style.maxHeight = '';
    menu.style.overflowY = '';
    wrap.classList.add('save-menu-down');
  }
  function _recentsPositionActionMenu(wrap) {
    const trigger = wrap?.querySelector?.('.sheet-item-action-menu-trigger');
    const menu = wrap?.querySelector?.('.sheet-item-action-menu');
    if (!trigger || !menu || typeof trigger.getBoundingClientRect !== 'function') return;
    const triggerRect = trigger.getBoundingClientRect();
    const sheetRect = recentsSheet?.getBoundingClientRect?.();
    const viewportHeight = typeof window !== 'undefined'
      ? window.innerHeight
      : document.documentElement.clientHeight;
    const gutter = 8;
    const lowerBound = Math.min(viewportHeight || 0, sheetRect?.bottom || viewportHeight || 0) - gutter;
    const upperBound = Math.max(0, sheetRect?.top || 0) + gutter;
    const spaceBelow = Math.max(0, lowerBound - triggerRect.bottom);
    const spaceAbove = Math.max(0, triggerRect.top - upperBound);
    const viewportWidth = typeof window !== 'undefined'
      ? window.innerWidth
      : document.documentElement.clientWidth;
    const menuWidth = Math.max(190, menu.offsetWidth || 190);
    const menuHeight = Math.max(1, menu.scrollHeight || menu.offsetHeight || 1);
    const openDown = spaceBelow >= menuHeight || spaceBelow >= spaceAbove;
    wrap.classList.toggle('save-menu-down', openDown);
    const availableSpace = openDown ? spaceBelow : spaceAbove;
    const left = Math.min(
      Math.max(gutter, triggerRect.right - menuWidth),
      Math.max(gutter, (viewportWidth || menuWidth) - menuWidth - gutter),
    );
    const top = openDown
      ? triggerRect.bottom + 4
      : Math.max(gutter, triggerRect.top - Math.min(menuHeight, Math.max(44, availableSpace)) - 4);
    menu.style.position = 'fixed';
    menu.style.left = `${left}px`;
    menu.style.top = `${top}px`;
    menu.style.right = 'auto';
    menu.style.bottom = 'auto';
    menu.style.width = `${menuWidth}px`;
    if (menuHeight > availableSpace && availableSpace > 0) {
      menu.style.maxHeight = `${Math.max(44, availableSpace)}px`;
      menu.style.overflowY = 'auto';
    } else {
      menu.style.maxHeight = '';
      menu.style.overflowY = '';
    }
  }
  function _recentsCopyCommand(run) {
    const command = run?.command || '';
    if (typeof copyTextToClipboard !== 'function') return;
    copyTextToClipboard(command)
      .then(() => showToast && showToast('Command copied'))
      .catch(() => showToast && showToast('Failed to copy command', 'error'));
  }
  function _recentsCanManageHistory() {
    return typeof activeTeamScopeCan === 'function'
      ? activeTeamScopeCan('manage_history')
      : true;
  }
  function _recentsCanMutateProjects() {
    return typeof activeTeamScopeCan === 'function'
      ? activeTeamScopeCan('mutate_projects')
      : true;
  }
  function _recentsRunActionMenu(run) {
    const wrap = document.createElement('div');
    wrap.className = 'sheet-item-action-menu-wrap save-menu-wrap save-menu-down';
    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'sheet-item-action sheet-item-action-menu-trigger btn btn-secondary btn-compact';
    trigger.textContent = 'more';
    trigger.setAttribute('aria-label', 'More run actions');
    trigger.setAttribute('aria-haspopup', 'menu');
    trigger.setAttribute('aria-expanded', 'false');
    const menu = document.createElement('div');
    menu.className = 'sheet-item-action-menu save-menu dropdown-surface';
    menu.setAttribute('role', 'menu');
    const addItem = (label, handler) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'dropdown-item dropdown-item-compact';
      item.setAttribute('role', 'menuitem');
      item.textContent = label;
      item.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        _recentsCloseActionMenus();
        handler();
      });
      menu.appendChild(item);
    };
    addItem('permalink', () => {
      if (!run.id) return;
      const url = `${location.origin}/history/${run.id}`;
      if (typeof shareUrl === 'function') {
        shareUrl(url).catch(() => showToast && showToast('Share failed', 'error'));
      }
    });
    addItem('compare', () => {
      if (_recentsOpenHistoryCompare(run, { returnFocus: recentPeek })) {
        closeRecentsSheet();
      } else if (showToast) {
        showToast('Run comparison is not available.', 'error');
      }
    });
    if (_recentsCanManageHistory()) {
      addItem('edit', () => {
        if (typeof historyEditEntityMetadata === 'function') historyEditEntityMetadata('run', run);
      });
    }
    if (_recentsCanMutateProjects()) {
      addItem('add to active project', () => {
        if (typeof historyAddRunToActiveProject === 'function') {
          historyAddRunToActiveProject(run)
            .catch(() => showToast && showToast('Failed to add run to active project', 'error'));
        }
      });
      addItem('add to project', () => {
        if (typeof historyAddRunToProject === 'function') {
          historyAddRunToProject(run)
            .catch(() => showToast && showToast('Failed to add run to project', 'error'));
        }
      });
    }
    addItem('copy run id', () => {
      if (typeof copyTextToClipboard === 'function') {
        copyTextToClipboard(run.id)
          .then(() => showToast && showToast('Run ID copied'))
          .catch(() => showToast && showToast('Failed to copy run ID', 'error'));
      }
    });
    if (_recentsCanManageHistory()) {
      addItem('delete', () => {
        if (run.id && typeof confirmHistAction === 'function') {
          confirmHistAction('delete', run.id, run.command, 'run');
        }
      });
    }
    bindPressable(trigger, {
      refocusComposer: false,
      clearPressStyle: true,
      onActivate: (event) => {
        event.preventDefault();
        event.stopPropagation();
        const open = !wrap.classList.contains('open');
        _recentsCloseActionMenus(open ? wrap : null);
        wrap.classList.toggle('open', open);
        trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) {
          _recentsPositionActionMenu(wrap);
        } else {
          _recentsResetActionMenuPosition(wrap);
        }
      },
    });
    wrap.append(trigger, menu);
    return wrap;
  }
  function _recentsMakeKindBadge(kind, label = kind.toUpperCase()) {
    const badge = document.createElement('span');
    const tone = kind === 'run' ? 'badge-tone-green' : 'badge-tone-muted';
    badge.className = `sheet-item-kind sheet-item-kind-${kind} badge ${tone}`;
    badge.textContent = label;
    return badge;
  }
  const RECENTS_GRACEFUL_TERMINATION_EXIT_CODES = new Set([-15]);
  function _recentsExitCodeNumber(exitCode) {
    if (exitCode === null || exitCode === undefined || exitCode === '') return null;
    const number = Number(exitCode);
    return Number.isFinite(number) ? number : null;
  }
  function _recentsIsGracefulTerminationExitCode(exitCode) {
    const code = _recentsExitCodeNumber(exitCode);
    return code !== null && RECENTS_GRACEFUL_TERMINATION_EXIT_CODES.has(code);
  }
  function _recentsIsFailedExitCode(exitCode) {
    const code = _recentsExitCodeNumber(exitCode);
    return code !== null && code !== 0 && !RECENTS_GRACEFUL_TERMINATION_EXIT_CODES.has(code);
  }
  function _recentsExitLabel(exitCode) {
    const code = _recentsExitCodeNumber(exitCode);
    if (code === null) return '—';
    return _recentsIsGracefulTerminationExitCode(code) ? 'terminated' : `exit ${code}`;
  }
  function _recentsSnapshotUrl(item) {
    return `${location.origin}/share/${item.id}`;
  }
  function _recentsOpenSnapshot(item) {
    if (!item || !item.id || typeof window === 'undefined' || !window || typeof window.open !== 'function') return;
    window.open(_recentsSnapshotUrl(item), '_blank', 'noopener,noreferrer');
  }
  function _recentsRenderList() {
    if (!recentsSheetList) return;
    recentsSheetList.replaceChildren();
    const starred = _recentsStarred();
    if (!_recentsItems.length) {
      const empty = document.createElement('div');
      empty.className = 'sheet-item chrome-row';
      empty.style.color = 'var(--muted)';
      empty.style.opacity = '0.7';
      empty.style.justifyContent = 'center';
      empty.style.alignItems = 'center';
      empty.textContent = _recentsHasActiveFilters()
        ? 'no matches'
        : _recentsFilterState.type === 'snapshots'
          ? 'no snapshots yet'
          : _recentsFilterState.type === 'runs'
            ? 'no recent commands'
            : 'no history yet';
      recentsSheetList.appendChild(empty);
      _recentsRenderPagination(0);
      return;
    }
    _recentsItems.forEach((entryData) => {
      const isRun = entryData.type !== 'snapshot';
      const run = isRun ? entryData : null;
      const snapshot = isRun ? null : entryData;
      const cmd = run?.command || '';
      const isStarred = isRun && starred.has(cmd);
      const item = document.createElement('div');
      item.className = 'sheet-item chrome-row';
      if (cmd) item.dataset.cmd = cmd;

      const head = document.createElement('div');
      head.className = 'sheet-item-head';
      const star = document.createElement('span');
      star.className = 'sheet-item-star' + (isStarred ? ' starred' : '');
      if (isRun) {
        star.textContent = isStarred ? '★' : '☆';
        star.setAttribute('role', 'button');
        star.setAttribute('tabindex', '0');
        const starLabel = isStarred
          ? 'Unstar — stop pinning this command to the top'
          : 'Star — keep this command pinned at the top';
        star.setAttribute('aria-label', starLabel);
        star.title = starLabel;
        bindPressable(star, {
          refocusComposer: false,
          clearPressStyle: true,
          onActivate: (e) => {
            e.stopPropagation();
            if (typeof toggleStar === 'function') {
              try { toggleStar(cmd); } catch (_) { /* non-critical */ }
            }
            _recentsRenderList();
            if (_recentsFilterState.starred) _recentsRefresh();
          },
        });
      } else {
        star.textContent = '⬚';
        star.setAttribute('aria-hidden', 'true');
      }
      const cmdEl = document.createElement('span');
      cmdEl.className = 'sheet-item-cmd';
      cmdEl.textContent = isRun ? cmd : (snapshot.label || 'snapshot');
      head.appendChild(star);
      head.appendChild(cmdEl);

      const meta = document.createElement('div');
      meta.className = 'sheet-item-meta';
      meta.appendChild(_recentsMakeKindBadge(isRun ? 'run' : 'snapshot'));
      const timeEl = document.createElement('span');
      timeEl.className = 'sheet-item-time';
      const parsed = _recentsParseDate(isRun ? run.started : snapshot.created);
      const relFn = typeof historyRelativeTime === 'function' ? historyRelativeTime : null;
      timeEl.textContent = parsed && relFn ? relFn(parsed) : '';
      if (parsed) timeEl.title = parsed.toLocaleString();
      meta.appendChild(timeEl);
      if (isRun) {
        const exitEl = document.createElement('span');
        const exitCode = (run.exit_code ?? null);
        exitEl.className = 'sheet-item-exit' + (_recentsIsFailedExitCode(exitCode) ? ' nonzero' : '');
        exitEl.textContent = _recentsExitLabel(exitCode);
        meta.appendChild(exitEl);
      }

      const actions = document.createElement('div');
      actions.className = 'sheet-item-actions';
      if (isRun) {
        actions.appendChild(_recentsMakeAction('copy command', () => _recentsCopyCommand(run)));
        actions.appendChild(_recentsMakeAction('restore', () => {
          if (typeof restoreHistoryRunIntoTab !== 'function') return;
          const cmdEl2 = item.querySelector('.sheet-item-cmd');
          if (cmdEl2) cmdEl2.textContent = 'loading…';
          restoreHistoryRunIntoTab(run, { hidePanelOnSuccess: false })
            .then(() => closeRecentsSheet())
            .catch(() => {
              if (cmdEl2) cmdEl2.textContent = cmd;
              if (typeof showToast === 'function') showToast('Failed to load run');
            });
        }));
        actions.appendChild(_recentsRunActionMenu(run));
      } else {
        actions.appendChild(_recentsMakeAction('open', () => {
          _recentsOpenSnapshot(snapshot);
          closeRecentsSheet();
        }));
        actions.appendChild(_recentsMakeAction('copy link', () => {
          if (typeof shareUrl === 'function') {
            shareUrl(_recentsSnapshotUrl(snapshot))
              .catch(() => showToast && showToast('Share failed', 'error'));
          }
        }));
      }
      if (!isRun) {
        if (_recentsCanManageHistory()) {
          actions.appendChild(_recentsMakeAction('delete', () => {
            if (!entryData.id) return;
            if (typeof confirmHistAction === 'function') {
              confirmHistAction('delete', entryData.id, snapshot.label, 'snapshot');
            }
          }));
        }
      }

      item.appendChild(head);
      item.appendChild(meta);
      item.appendChild(actions);

      item.addEventListener('click', (e) => {
        if (e.target.closest('.sheet-item-action, .sheet-item-star, .sheet-item-action-menu-wrap')) return;
        _recentsCloseActionMenus();
        if (!isRun) {
          _recentsOpenSnapshot(snapshot);
          closeRecentsSheet();
          return;
        }
        if (_recentsOpenRunDetails(run)) {
          closeRecentsSheet();
          return;
        }
        if (typeof setComposerValue === 'function') setComposerValue(cmd, cmd.length, cmd.length);
        closeRecentsSheet();
      });

      recentsSheetList.appendChild(item);
    });
    _recentsRenderPagination(_recentsItems.length);
  }
  function _recentsRenderLoading() {
    if (!recentsSheetList) return;
    recentsSheetList.replaceChildren();
    const loading = document.createElement('div');
    loading.className = 'sheet-item chrome-row';
    loading.style.color = 'var(--muted)';
    loading.style.opacity = '0.7';
    loading.style.justifyContent = 'center';
    loading.style.alignItems = 'center';
    loading.textContent = 'loading history...';
    recentsSheetList.appendChild(loading);
    _recentsRenderPagination(0);
  }
  function _recentsRefresh({ render = true } = {}) {
    if (typeof apiFetch !== 'function') return Promise.resolve([]);
    const requestUrl = _recentsBuildHistoryRequestUrl();
    const requestSeq = ++_recentsRequestSeq;
    const request = apiFetch(requestUrl)
      .then(r => r.json())
      .then(data => {
        if (requestSeq !== _recentsRequestSeq) return _recentsItems;
        _recentsPaging.page = Math.max(1, Number(data.page) || _recentsPaging.page || 1);
        _recentsPaging.pageSize = Math.max(1, Number(data.page_size) || _recentsPaging.pageSize || 1);
        _recentsPaging.totalCount = Math.max(0, Number(data.total_count ?? data.items?.length ?? data.runs?.length ?? 0) || 0);
        _recentsPaging.pageCount = Math.max(0, Number(data.page_count) || 0);
        _recentsPaging.hasPrev = !!data.has_prev;
        _recentsPaging.hasNext = !!data.has_next;
        _recentsItems = Array.isArray(data.items) ? data.items : (Array.isArray(data.runs) ? data.runs : []);
        _recentsLoaded = true;
        if (render || isRecentsSheetOpen()) _recentsRenderList();
        return _recentsItems;
      })
      .catch(() => {
        if (requestSeq !== _recentsRequestSeq) return _recentsItems;
        _recentsItems = [];
        _recentsLoaded = false;
        _recentsPaging.totalCount = 0;
        _recentsPaging.pageCount = 0;
        _recentsPaging.hasPrev = false;
        _recentsPaging.hasNext = false;
        if (render || isRecentsSheetOpen()) _recentsRenderList();
        return [];
      });
    _recentsFetchInFlight = request.finally(() => {
      if (_recentsFetchInFlight === request && requestSeq === _recentsRequestSeq) _recentsFetchInFlight = null;
    });
    return _recentsFetchInFlight;
  }
  function _recentsPrefetch() {
    if (_recentsLoaded || _recentsFetchInFlight) return _recentsFetchInFlight || Promise.resolve(_recentsItems);
    return _recentsRefresh({ render: false });
  }
  function showRecentsSheet() {
    if (!recentsSheet) return;
    _recentsSearchQuery = '';
    if (recentsSheetSearch) recentsSheetSearch.value = '';
    if (typeof blurVisibleComposerInputIfMobile === 'function') {
      try { blurVisibleComposerInputIfMobile(); } catch (_) { /* non-critical */ }
    }
    // Reset filter UI each open so users don't inherit stale state.
    _recentsFilterState.type = 'all';
    _recentsFilterState.root = '';
    _recentsFilterState.exit = 'all';
    _recentsFilterState.date = 'all';
    _recentsFilterState.starred = false;
    _recentsPaging.page = 1;
    if (recentsFiltersToggle) recentsFiltersToggle.setAttribute('aria-expanded', 'false');
    if (recentsFiltersExpanded) recentsFiltersExpanded.classList.add('u-hidden');
    _recentsSyncFilterUI();
    _recentsRenderLoading();
    show(recentsSheetScrim);
    show(recentsSheet);
    _recentsRefresh();
  }
  function closeRecentsSheet() {
    _closeRecentsDropdowns();
    _recentsCloseActionMenus();
    hide(recentsSheet);
    hide(recentsSheetScrim);
  }
  function isRecentsSheetOpen() {
    return !!(recentsSheet && recentsSheet.classList && !recentsSheet.classList.contains('u-hidden'));
  }

  // Scrim click + Escape are owned by bindDismissible so
  // the sheet participates in the unified modal > sheet > panel Escape
  // cascade (see ui_dismissible.js).
  if (typeof bindDismissible === 'function') {
    bindDismissible(recentsSheet, {
      level: 'sheet',
      isOpen: isRecentsSheetOpen,
      onClose: closeRecentsSheet,
      backdropEl: recentsSheetScrim,
    });
  }
  if (recentsSheetClearBtn) {
    bindPressable(recentsSheetClearBtn, {
      refocusComposer: false,
      clearPressStyle: true,
      onActivate: () => {
        if (!_recentsCanManageHistory()) return;
        if (typeof confirmHistAction === 'function') {
          confirmHistAction('clear', null, null, 'run', _recentsDeleteContext());
        }
      },
    });
  }

  if (typeof onUiEvent === 'function') {
    onUiEvent('app:history-panel-refreshed', () => {
      if (isRecentsSheetOpen()) _recentsRefresh();
    });
    onUiEvent('app:history-mutated', () => {
      if (isRecentsSheetOpen()) _recentsRefresh();
    });
  }
  _recentsPrefetch();

  let _recentsSearchTimer = null;
  recentsSheetSearch?.addEventListener('input', (e) => {
    _recentsSearchQuery = e.target.value || '';
    if (_recentsSearchTimer) clearTimeout(_recentsSearchTimer);
    _recentsSearchTimer = setTimeout(() => {
      _recentsPaging.page = 1;
      _recentsRefresh();
    }, 100);
  });

  recentsSheetList?.addEventListener('touchmove', () => {
    if (recentsSheetSearch && document.activeElement === recentsSheetSearch) {
      recentsSheetSearch.blur();
    }
  }, { passive: true });

  // Drag/tap/keyboard close behavior is provided by the shared bindMobileSheet
  // helper (see app/static/js/ui/mobile_sheet.js) so the recents sheet matches
  // every other mobile bottom sheet.
  if (typeof bindMobileSheet === 'function') {
    bindMobileSheet(recentsSheet, { onClose: closeRecentsSheet });
  }

  const recentsFiltersToggle   = document.getElementById('mobile-recents-filters-toggle');
  const recentsFiltersExpanded = document.getElementById('mobile-recents-filters-expanded');
  const recentsFiltersClear    = document.getElementById('mobile-recents-filters-clear');
  const recentsFilterRoot      = document.getElementById('mobile-recents-filter-root');
  const recentsFilterStarred   = recentsSheet?.querySelector('[data-recents-filter="starred"]') || null;
  const recentsDropdowns       = Array.from(recentsSheet?.querySelectorAll('[data-recents-dropdown]') || []);
  const recentsChipsEl         = document.getElementById('mobile-recents-chips');
  const _dropdownLabels = {
    type: { all: 'all', runs: 'runs', snapshots: 'snapshots' },
    exit: { all: 'all', success: 'success (0)', failed: 'failed' },
    date: { all: 'all', today: 'today', week: 'this week' },
  };
  // Short labels used inside the active-filter chips (desktop uses the same
  // pattern: shorter inside chips than inside the filter rows).
  const _chipLabels = {
    type: { runs: 'runs', snapshots: 'snapshots' },
    exit: { success: 'exit 0', failed: 'failed' },
    date: { today: 'today', week: 'past week' },
  };

  function _recentsResetRunOnlyFilters() {
    _recentsFilterState.root = '';
    _recentsFilterState.exit = 'all';
    _recentsFilterState.starred = false;
  }

  function _clearOneFilter(key) {
    if (key === 'type')    _recentsFilterState.type = 'all';
    if (key === 'root')    _recentsFilterState.root = '';
    if (key === 'exit')    _recentsFilterState.exit = 'all';
    if (key === 'date')    _recentsFilterState.date = 'all';
    if (key === 'starred') _recentsFilterState.starred = false;
    _recentsSyncFilterUI();
    _recentsPaging.page = 1;
    _recentsRefresh();
  }

  function _renderRecentsChips() {
    if (!recentsChipsEl) return;
    recentsChipsEl.replaceChildren();
    const push = (key, text) => {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'filter-chip chip chip-removable';
      chip.dataset.chipKey = key;
      chip.setAttribute('aria-label', `Clear filter ${text}`);
      const label = document.createElement('span');
      label.textContent = text;
      const x = document.createElement('span');
      x.className = 'filter-chip-x';
      x.textContent = '×';
      chip.append(label, x);
      bindPressable(chip, {
        refocusComposer: false,
        clearPressStyle: true,
        onActivate: () => _clearOneFilter(key),
      });
      recentsChipsEl.appendChild(chip);
    };
    const s = _recentsFilterState;
    if (s.type !== 'all')      push('type',    _chipLabels.type[s.type] || s.type);
    if (s.root.trim())        push('root',    `command: ${s.root.trim()}`);
    if (s.exit !== 'all')     push('exit',    _chipLabels.exit[s.exit] || s.exit);
    if (s.date !== 'all')     push('date',    _chipLabels.date[s.date] || s.date);
    if (s.starred)            push('starred', 'starred');
  }

  function _closeRecentsDropdowns(except) {
    recentsDropdowns.forEach(wrap => {
      if (wrap === except) return;
      wrap.classList.remove('open');
      wrap.querySelector('.sheet-filter-dropdown')?.setAttribute('aria-expanded', 'false');
    });
  }

  function _recentsSyncFilterUI() {
    const runOnlyEnabled = _recentsFilterState.type !== 'snapshots';
    if (recentsSheetClearBtn) {
      const showClear = runOnlyEnabled && _recentsCanManageHistory();
      recentsSheetClearBtn.classList.toggle('u-hidden', !showClear);
      recentsSheetClearBtn.disabled = !showClear;
    }
    if (recentsFilterRoot) {
      recentsFilterRoot.value = _recentsFilterState.root;
      recentsFilterRoot.closest('.sheet-filter-row')?.classList.toggle('active', !!_recentsFilterState.root.trim());
      recentsFilterRoot.disabled = !runOnlyEnabled;
    }
    recentsDropdowns.forEach(wrap => {
      const key = wrap.dataset.recentsDropdown;
      const val = _recentsFilterState[key] || 'all';
      const labelMap = _dropdownLabels[key] || {};
      const labelEl = wrap.querySelector('[data-dropdown-label]');
      const trigger = wrap.querySelector('.sheet-filter-dropdown');
      if (labelEl) labelEl.textContent = labelMap[val] || val;
      wrap.classList.toggle('active', val !== 'all');
      if (trigger) trigger.disabled = !runOnlyEnabled && key === 'exit';
      wrap.querySelectorAll('[data-dropdown-value]').forEach(opt => {
        const active = opt.dataset.dropdownValue === val;
        opt.setAttribute('aria-selected', active ? 'true' : 'false');
        opt.classList.toggle('dropdown-item-active', active);
      });
    });
    if (recentsFilterStarred) {
      recentsFilterStarred.setAttribute('aria-pressed', _recentsFilterState.starred ? 'true' : 'false');
      recentsFilterStarred.disabled = !runOnlyEnabled;
    }
    if (recentsFiltersToggle) {
      const count = _recentsFiltersActiveCount();
      const open = recentsFiltersToggle.getAttribute('aria-expanded') === 'true';
      const labelEl = recentsFiltersToggle.querySelector('.sheet-filter-toggle-label');
      const text = (open ? 'hide filters' : 'filters') + (count ? ` (${count})` : '');
      if (labelEl) labelEl.textContent = text;
      else recentsFiltersToggle.textContent = text;
    }
    _renderRecentsChips();
  }

  if (recentsFiltersToggle) {
    bindDisclosure(recentsFiltersToggle, {
      panel: recentsFiltersExpanded,
      openClass: null,
      hiddenClass: 'u-hidden',
      clearPressStyle: true,
      onToggle: (open) => {
        if (!open) _closeRecentsDropdowns();
        // _recentsSyncFilterUI() rewrites the toggle label ("filters" vs
        // "hide filters") using the just-synced aria-expanded value, so it
        // must run after the helper's sync(), which is already the order
        // onToggle fires in.
        _recentsSyncFilterUI();
      },
    });
  }

  let _recentsRootTimer = null;
  recentsFilterRoot?.addEventListener('input', (e) => {
    _recentsFilterState.root = e.target.value || '';
    if (_recentsRootTimer) clearTimeout(_recentsRootTimer);
    _recentsRootTimer = setTimeout(() => {
      _recentsSyncFilterUI();
      _recentsPaging.page = 1;
      _recentsRefresh();
    }, 100);
  });

  recentsDropdowns.forEach(wrap => {
    const key = wrap.dataset.recentsDropdown;
    const trigger = wrap.querySelector('.sheet-filter-dropdown');
    if (trigger) {
      bindPressable(trigger, {
        refocusComposer: false,
        clearPressStyle: true,
        onActivate: () => {
          const open = wrap.classList.contains('open');
          _closeRecentsDropdowns(open ? null : wrap);
          wrap.classList.toggle('open', !open);
          trigger.setAttribute('aria-expanded', !open ? 'true' : 'false');
        },
      });
    }
    wrap.querySelectorAll('[data-dropdown-value]').forEach(opt => {
      bindPressable(opt, {
        refocusComposer: false,
        clearPressStyle: true,
        onActivate: () => {
          _recentsFilterState[key] = opt.dataset.dropdownValue;
          if (key === 'type' && _recentsFilterState.type === 'snapshots') _recentsResetRunOnlyFilters();
          wrap.classList.remove('open');
          trigger?.setAttribute('aria-expanded', 'false');
          _recentsSyncFilterUI();
          _recentsPaging.page = 1;
          _recentsRefresh();
        },
      });
    });
  });

  // Close dropdowns on ambient click anywhere in the recents sheet that
  // doesn't land inside a dropdown. bindOutsideClickClose owns the trigger
  // exemption: clicks on the dropdown triggers / option items bubble up but
  // are skipped because they're inside [data-recents-dropdown].
  if (recentsSheet && typeof bindOutsideClickClose === 'function') {
    bindOutsideClickClose(null, {
      scope: recentsSheet,
      isOpen: () => recentsDropdowns.some(w => w.classList.contains('open')),
      onClose: () => _closeRecentsDropdowns(),
      exemptSelectors: ['[data-recents-dropdown]'],
    });
  }

  if (recentsFilterStarred) {
    bindPressable(recentsFilterStarred, {
      refocusComposer: false,
      clearPressStyle: true,
      onActivate: () => {
        _recentsFilterState.starred = !_recentsFilterState.starred;
        _recentsSyncFilterUI();
        _recentsPaging.page = 1;
        _recentsRefresh();
      },
    });
  }

  if (recentsFiltersClear) {
    bindPressable(recentsFiltersClear, {
      refocusComposer: false,
      clearPressStyle: true,
      onActivate: () => {
        _recentsFilterState.type = 'all';
        _recentsFilterState.root = '';
        _recentsFilterState.exit = 'all';
        _recentsFilterState.date = 'all';
        _recentsFilterState.starred = false;
        _recentsPaging.page = 1;
        _closeRecentsDropdowns();
        _recentsSyncFilterUI();
        _recentsRefresh();
      },
    });
  }

  // Escape-to-close is owned by bindDismissible's unified dispatcher
  // (closeTopmostDismissible). The sheets are registered above so they
  // participate in the same modal > sheet > panel cascade as every
  // other surface.

  // Peek: tap opens the sheet; vertical swipe-up also opens it.
  function openPeekSurface(event) {
    if (event && typeof event.stopPropagation === 'function') event.stopPropagation();
    if (recentPeek && recentPeek.dataset.peekMode === 'status-monitor') {
      if (typeof importedOpenStatusMonitor === 'function') void importedOpenStatusMonitor({ source: 'mobile-peek' });
      return;
    }
    openMobileHistorySurface();
  }
  if (recentPeek) {
    // role="button" div — Enter/Space handled by bindPressable; opt into
    // clearPressStyle so the :hover/:active residue on touch doesn't stick
    // after activation (native blur is a no-op on non-focusable elements).
    bindPressable(recentPeek, {
      refocusComposer: false,
      clearPressStyle: true,
      onActivate: openPeekSurface,
    });
  }

  if (recentPeek) {
    let peekStartY = null;
    recentPeek.addEventListener('pointerdown', (e) => { peekStartY = e.clientY; });
    recentPeek.addEventListener('pointermove', (e) => {
      if (peekStartY === null) return;
      const dy = peekStartY - e.clientY;
      if (dy > 8) {
        peekStartY = null;
        openPeekSurface();
      }
    });
    const endPeekDrag = () => { peekStartY = null; };
    recentPeek.addEventListener('pointerup', endPeekDrag);
    recentPeek.addEventListener('pointercancel', endPeekDrag);
  }

  if (typeof onUiEvent === 'function') {
    onUiEvent('app:history-rendered', () => {
      try { renderRecentPeek(); } catch (_) { /* non-critical */ }
      if (isMenuSheetOpen()) {
        try { refreshHistoryCount(); } catch (_) { /* non-critical */ }
      }
    });
    onUiEvent('app:tab-status-changed', (e) => {
      const activeId = typeof getActiveTabId === 'function' ? getActiveTabId() : null;
      const activeTab = typeof getActiveTab === 'function' ? getActiveTab() : null;
      const detail = e && e.detail ? e.detail : {};
      const suppressStatusMonitorHold = !!(activeTab && activeTab.suppressStatusMonitorPeekHold);
      if (detail.id === activeId && detail.status && detail.status !== 'running' && suppressStatusMonitorHold) {
        _statusMonitorPeekHoldUntil = 0;
      } else if (detail.id === activeId && detail.status && detail.status !== 'running') {
        _statusMonitorPeekHoldUntil = Date.now() + 2500;
        window.setTimeout(() => {
          try { renderRecentPeek(); } catch (_) { /* non-critical */ }
        }, 2550);
      }
      try { renderRecentPeek(); } catch (_) { /* non-critical */ }
      if (isMenuSheetOpen()) {
        try { refreshHistoryCount(); } catch (_) { /* non-critical */ }
        try { refreshCompareActiveAction(); } catch (_) { /* non-critical */ }
      }
    });
    onUiEvent('app:tab-activated', () => {
      _statusMonitorPeekHoldUntil = 0;
      try { renderRecentPeek(); } catch (_) { /* non-critical */ }
      if (isMenuSheetOpen()) {
        try { refreshCompareActiveAction(); } catch (_) { /* non-critical */ }
      }
    });
  }
  renderRecentPeek();

  // Mirror the workflows list count into the menu-sheet hint so "workflows"
  // advertises how many are available without opening the overlay.
  if (typeof onUiEvent === 'function') {
    onUiEvent('app:workflows-rendered', (e) => {
      try { refreshWorkflowsCount(e.detail && e.detail.items); } catch (_) { /* non-critical */ }
    });
    onUiEvent('app:schedules-rendered', (e) => {
      try { refreshSchedulesCount(e.detail && e.detail.items); } catch (_) { /* non-critical */ }
    });
    onUiEvent('app:watchers-rendered', (e) => {
      try { refreshWatchersCount(e.detail && e.detail.items); } catch (_) { /* non-critical */ }
    });
    onUiEvent('app:active-project-changed', (e) => {
      try { refreshProjectHint(e.detail && e.detail.project); } catch (_) { /* non-critical */ }
    });
  }

  // ── 2E: Keyboard helper row ─────────────────────────────────────
  function syncKbHelper() {
    const open = !!(document.body && document.body.classList
                    && document.body.classList.contains('mobile-keyboard-open'));
    if (open) {
      show(kbHelper);
    } else {
      hide(kbHelper);
    }
  }
  if (typeof onUiEvent === 'function') {
    onUiEvent('app:mobile-keyboard-state', () => syncKbHelper());
  }
  syncKbHelper();

  // Invoke the shared edit action directly. preventDefault on pointerdown keeps
  // the composer input from losing focus when a helper key is tapped.
  kbHelper?.querySelectorAll('button[data-kb-action]').forEach(btn => {
    const action = btn.dataset.kbAction;
    const fire = () => {
      if (typeof performMobileEditAction === 'function') {
        try { performMobileEditAction(action); } catch (_) { /* non-critical */ }
      }
    };
    btn.addEventListener('pointerdown', (e) => { e.preventDefault(); fire(); });
    btn.addEventListener('mousedown',   (e) => { e.preventDefault(); });
    btn.addEventListener('click',       (e) => { e.preventDefault(); });
  });

  // ── Pull-to-refresh suppression ───────────────────────────────────
  // The CSS rule `overscroll-behavior-y: contain` (in mobile-chrome.css) does
  // not actually disable iOS Safari's or Firefox mobile's native pull-to-
  // refresh in this layout. Both engines only honour the property when the
  // element it is set on is itself a non-trivial scroll container — and the
  // mobile shell deliberately keeps body content within the viewport so the
  // body never grows tall enough to scroll. The browsers therefore treat the
  // downward swipe as a navigation-level gesture before any container can
  // claim it.
  //
  // The fix is a delegated touchmove guard: when in mobile-terminal-mode, walk
  // the touch target's ancestor chain looking for a scrollable element that
  // can absorb the gesture. If one exists and isn't already at the boundary
  // for the current direction, let the gesture through. Otherwise call
  // preventDefault on the touchmove so the browser cannot interpret it as
  // pull-to-refresh / overscroll bounce. This intentionally does not interfere
  // with the sheet drag handlers in mobile_sheet.js, which run on Pointer
  // Events with setPointerCapture and `touch-action: none` on the grab — those
  // already bypass the browser's default touch handling.
  let _touchStartX = null;
  let _touchStartY = null;
  document.addEventListener('touchstart', (e) => {
    if (e.touches.length === 1) {
      _touchStartX = e.touches[0].clientX;
      _touchStartY = e.touches[0].clientY;
    } else {
      _touchStartX = null;
      _touchStartY = null;
    }
  }, { passive: true });
  document.addEventListener('touchmove', (e) => {
    if (!document.body.classList.contains('mobile-terminal-mode')) return;
    if (_touchStartY == null || e.touches.length !== 1) return;
    const dy = e.touches[0].clientY - _touchStartY;
    const dx = e.touches[0].clientX - _touchStartX;
    if (dy === 0) return;
    // Predominantly horizontal gestures are never pull-to-refresh
    // candidates; bailing out here lets horizontal scroll containers
    // (e.g. the mobile tab bar, with overflow-y:hidden so the vertical
    // walk below wouldn't find them) receive the full gesture.
    if (Math.abs(dx) >= Math.abs(dy)) return;
    let el = e.target;
    while (el && el !== document.body && el !== document.documentElement) {
      if (el.scrollHeight > el.clientHeight) {
        const oy = getComputedStyle(el).overflowY;
        if (oy === 'auto' || oy === 'scroll') {
          // Scrolling down (dy > 0) is only "would-overscroll" at scrollTop 0.
          // Scrolling up (dy < 0) is only "would-overscroll" at the bottom.
          if (dy > 0 && el.scrollTop > 0) return;
          if (dy < 0 && el.scrollTop + el.clientHeight < el.scrollHeight) return;
          break;
        }
      }
      el = el.parentElement;
    }
    if (e.cancelable) e.preventDefault();
  }, { passive: false });

})(typeof window !== 'undefined' ? window : this);
